import { Doc, toDoc } from '@dadohr/shared/domainTypes/document';
import { TestAccount } from '@dadohr/shared/domainTypes/testAccount';
import { FS } from '@dadohr/shared/versions';
import { compact } from 'lodash';
import { useMemo } from 'react';
import {
  AdditionActionsMenuOption,
  AdditionalActionsMenu
} from '../../../../../components/AdditionalActionsMenu';
import { Check, Cross } from '../../../../../components/Check';
import {
  ID_TO_ROW_KEY,
  ItemSorter,
  RowsRenderer,
  ROW_HEIGHTS
} from '../../../../../components/GroupableList';
import { Loader } from '../../../../../components/Loader';
import { IColumn } from '../../../../../components/Table/VirtualizedSortable';
import { Page } from '../../../../../layout/Page';
import { Section } from '../../../../../layout/Section';
import { store } from '../../../../../services/firebase';
import {
  CollectionListener,
  createSingleCollectionListenerStore,
  useCollectionListener
} from '../../../../../services/firecache/collectionListener';

type ColumnName = 'email' | 'available' | 'actions';
type D = Doc<TestAccount>;
type Column = IColumn<D, ColumnName>;

const Actions = ({ d }: { d: D }) => {
  const options: AdditionActionsMenuOption[] = useMemo(() => {
    return compact([
      !d.data.available && {
        key: 'makeAvailable',
        label: 'Make available',
        onClick: () =>
          store()
            .collection(FS.testAccounts)
            .doc(d.id)
            .update({ available: true })
      },
      d.data.available && {
        key: 'makeUnavailable',
        label: 'Make unavailable',
        onClick: () =>
          store()
            .collection(FS.testAccounts)
            .doc(d.id)
            .update({ available: false })
      }
    ]);
  }, [d]);
  return <AdditionalActionsMenu options={options} />;
};

const COLUMNS: Column[] = [
  {
    key: 'email',
    head: () => 'Email',
    cell: (d) => d.data.email,
    align: 'left',
    sortable: false,
    defaultDirection: 'asc',
    width: 200,
    flexGrow: 2
  },
  {
    key: 'available',
    head: () => 'available',
    cell: (d) => (d.data.available ? <Check /> : <Cross />),
    align: 'left',
    sortable: false,
    defaultDirection: 'asc',
    width: 150,
    flexGrow: 1
  },
  {
    key: 'actions',
    head: () => '',
    cell: (d) => <Actions d={d} />,
    align: 'right',
    defaultDirection: 'asc',
    width: 100,
    flexGrow: 0
  }
];

const SORTER: ItemSorter<D> = {
  key: 'email',
  items: { sort: (d) => d.data.email, dir: 'asc' }
};

const testAccountsStore = createSingleCollectionListenerStore(
  () =>
    new CollectionListener(store().collection(FS.testAccounts), (s) =>
      toDoc<TestAccount>(s)
    )
);
const useTestAccounts = () => useCollectionListener(testAccountsStore());

export const PageTestAccountsOverview = () => {
  const [testAccounts, loading] = useTestAccounts();

  return (
    <Page width="M">
      <Section>
        {loading && <Loader height={500} />}
        {testAccounts && (
          <RowsRenderer
            variant="contained"
            columns={COLUMNS}
            rows={testAccounts}
            rowToKey={ID_TO_ROW_KEY}
            sorter={SORTER}
            sortDirection={SORTER.items.dir}
            renderHead={true}
            chunkSize={30}
            rootMargin="300px"
            rowHeight={ROW_HEIGHTS.dense}
            otherProps={undefined}
          />
        )}
      </Section>
    </Page>
  );
};
